import React, { useState, useEffect } from 'react';
import { Box, Typography, Slider, Button, Tooltip, ClickAwayListener, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DoughnutChart from './DoughnutChart';
import { generateTestId } from '../../../../app/functions';
import { getMediaPath } from '../../../../app/functions';
import HtmlContent from 'components/html/HtmlContent';

const SectionWrapper = styled(Box)(({ theme }) => ({
  padding: '0 0',
  [theme.breakpoints.up('sm')]: {
    padding: '0 0',
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  padding: '0 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '40px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.up('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.up('lg')]: {
    padding: '60px',
  },
  [theme.breakpoints.up('xl')]: {
    padding: '80px',
    maxWidth: '1440px',
  },
}));

const CalculatorContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.common.white,
  // paddingBottom: '40px',
  borderRadius: '24px',
  width: '572px'  ,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0px',
  '& > *:nth-of-type(3)': { // This targets the AmountDisplay
    marginBottom: '44px',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },

}));

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.up('lg')]: {
    padding: '30px',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.savingsSection.title,
  fontSize: '24px',
  marginBottom: '8px',
  fontWeight: 700,
  lineHeight: 'normal',
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    fontSize: '32px',
    marginBottom: '10px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '32px',
    marginBottom: '12px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '38px',
    marginBottom: '16px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '46px',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.savingsSection.subtitle,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 'normal',
  textAlign: 'center',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '20px',
    marginBottom: '20px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    marginBottom: '24px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '24px',
    marginBottom: '28px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '28px',
  },
}));

const AmountContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
  [theme.breakpoints.up('sm')]: {
    gap: '24px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '24px',
  },
  [theme.breakpoints.up('lg')]: {
    gap: '32px',
  },
  [theme.breakpoints.up('xl')]: {
    gap: '40px',
  },
}));

const SavingAmountContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
}));
const AmountDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  width: '150px',
  lineHeight: 'normal',
  position: 'relative',
  alignSelf: 'center',
  gap: '4px',
  [theme.breakpoints.up('sm')]: {
    width: '160px',
  },
  [theme.breakpoints.up('md')]: {
    width: '200px',
  },
  [theme.breakpoints.up('lg')]: {
    width: 'auto',
    display: 'flex',
    justifyContent: 'center',
    borderBottom: '2px solid',
  },
  [theme.breakpoints.up('xl')]: {
    width: '300px',
  },
}));

const CurrencySymbol = styled(Typography)(({ theme }) => ({
  color: theme.palette.savingsSection.amount,
  letterSpacing: '-0.036px',
  width: '35px',
  height: '37px',
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: 'normal',
  marginBottom: '0',
  [theme.breakpoints.up('sm')]: {
    width: '40px',
    height: '40px',
    fontSize: '28px',
  },
  [theme.breakpoints.up('md')]: {
    width: '40px',
    height: '40px',
    fontSize: '25px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '50px',
    height: '50px',
    fontSize: '36px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '60px',
    height: '60px',
    fontSize: '40px',
  },
}));

const Amount = styled(Typography)(({ theme }) => ({
  color: theme.palette.savingsSection.amount,
  fontSize: '36px',
  fontWeight: 700,
  lineHeight: 'normal',
  letterSpacing: '-0.36px',
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    fontSize: '44px',
    letterSpacing: '-0.4px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '44px',
    letterSpacing: '-0.48px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '55px',
    letterSpacing: '-0.52px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '60px',
    letterSpacing: '-0.6px',
  },
}));

const SliderTextsContainer = styled(Box)(({ theme }) => ({
  gap: '0px',
}));

const CustomSlider = styled(Slider)(({ theme }) => ({
  width: '300px',
  transform: 'rotate(180deg)',
  [theme.breakpoints.up('sm')]: {
    width: '350px',
  },
  [theme.breakpoints.up('md')]: {
    width: '320px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '450px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '500px',
  },
  '& .MuiSlider-rail': {
    backgroundColor: theme.palette.savingsSection.slider,
    height: '12px',
    borderRadius: '24px',
  },
  '& .MuiSlider-track': {
    backgroundColor: theme.palette.savingsSection.sliderTrack,
    height: '12px',
    borderRadius: '24px',
  },
  '& .MuiSlider-thumb': {
    width: '24px',
    height: '24px',
    backgroundColor: theme.palette.savingsSection.sliderThumb,
    '&:hover, &.Mui-focusVisible': {
      boxShadow: 'none',
    },
  },
}));

const JoinButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #7A40E2 0%, #3866E5 100%)',
  borderRadius: '24px',
  padding: '10px 40px',
  width: '100%',
  color: theme.palette.common.white,
  fontSize: '16px',
  fontWeight: 700,
  textTransform: 'uppercase',
  '&:hover': {
    background: 'linear-gradient(90deg, #8533EB 0%, #4001BF 100%)',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '20px',
    width: '70%',
    padding: '10px 40px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '18px',
    width: '70%',
    padding: '8px 20px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
    width: '70%',
    padding: '8px 100px',
  },
}));


const HelpText = styled(Box)(({  theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  opacity: 1 ,
  [theme.breakpoints.down('md')]: {
    marginBottom: '8px',
    '& .MuiTypography-root': {
      fontSize: '12px',
    },
    '& .MuiIconHot-root': {
      fontSize: '16px',
    }
  },
}));


const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#FFFFFF',
    color: '#29286A',
    fontSize: '16px',
    padding: '16px',
    boxShadow: '0px 4px 44px rgba(48, 94, 138, 0.2)',
    borderRadius: '12px',
    maxWidth: '300px',
    textAlign: 'center',
    opacity: 1,
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '22px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '24px',
  },
}));

const SliderLabels = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0px',
  margin: '0px',
  color: '#29286A',
  fontSize: '14px',
  fontWeight: 400,
  '& .MuiTypography-root': {
    lineHeight: 'normal',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '22px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '24px',
  },
}));

const CustomInput = styled('input')(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 700,
  textAlign: 'center',
  width: '160px',
  border: 'none',
  borderBottom: 'none',
  outline: 'none',
  color: theme.palette.savingsSection.amount,
  [theme.breakpoints.up('sm')]: {
    fontSize: '44px',
    width: '150px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '48px',
    width: '160px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '52px',
    width: '170px',
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '56px',
    width: '175px',
  },
}));

// Function to format numbers with commas
const formatNumberWithCommas = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const SavingsSection = ({ onClick, data, testID, joinButtonText }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Calculate initial money spent amount based on the average of the minimum and maximum in the bar
  const calculateInitialAmount = () => {
    if (data.barMinimum === 0) {
      return data.barMaximum / 2;
    }
    return data.barMinimum + data.barMaximum / 2 -  data.barMinimum;
  };
  
  // Get slider range from data
  const getSliderRange = () => {
    if (!data) return { min: 1000, max: 20000 };

    return {
      min: data.barMinimum ? data.barMinimum : 1000,
      max: data.barMaximum ? data.barMaximum : 20000
    };
  };
  const [amount, setAmount] = useState(calculateInitialAmount());
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const sliderRange = getSliderRange();

  const handleTooltipClose = (event) => {
    if (event && event.target.closest('[testId="desktop-help-text"], [testId="desktop-help-tooltip"]')) {
      return;
    }
    setTooltipOpen(false);
  };

  const handleTooltipToggle = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const handleAmountChange = (event, newValue) => {
    // Adjust the value to account for the inverted direction
    const adjustedValue = sliderRange.max - (newValue - sliderRange.min);
    // Ensure the adjusted value does not exceed the maximum
    const constrainedValue = Math.min(adjustedValue, sliderRange.max);
    setAmount(constrainedValue);
  };

  // Calculate total savings based on the amount
  const calculateYearlySavings = (monthlyAmount) => {
    // Assuming 12 months in a year
    return monthlyAmount * 12;
  };

  const getTotalSavings = (amount, categories) => {
    const initialSavings = 240;
    const yearlysavings = calculateYearlySavings(amount);
    let sum = 0;
    categories.forEach(category => {
      const result = (category.percentage / 100) * yearlysavings * (category.averagePercentage / 100);
      sum += Math.round(result)
    });
    return sum + initialSavings;
  };
  
  const totalSavings = getTotalSavings(amount, data.doughnutChartCategories);



  // Prepare slices data for the doughnut chart
  const prepareSlices = () => {
    if (!data || !data.doughnutChartCategories) return [];
    

    // Create slices with adjusted amounts based on the user's input
    const yearlysavings = calculateYearlySavings(amount);
    return data.doughnutChartCategories.map(category => ({
      text: category.text,
      amount: Math.round((category.percentage / 100) * yearlysavings * (category.averagePercentage / 100)),
      icon: getMediaPath(category.imagePath)
    }));
  };

  const slices = prepareSlices();

  // If data is not available, show a loading state or return null
  if (!data) {
    return null;
  }

  return (
    <SectionWrapper testID={generateTestId(testID, 'section-wrapper')}>
      <ContentWrapper testID={generateTestId(testID, 'content-wrapper')}>
        <CalculatorContainer testID={generateTestId(testID, 'calculator-container')}>
          <Title testID={generateTestId(testID, 'title')}>{data.title}</Title>
          <Subtitle testID={generateTestId(testID, 'subtitle')}>{data.subtitle}</Subtitle>
          <AmountContainer testID={generateTestId(testID, 'amount-container')}>
          <SavingAmountContainer testID={generateTestId(testID, 'saving-amount-container')}>
            <AmountDisplay testID={generateTestId(testID, 'amount-display')}>
              <CustomInput
                type="text"
                value={formatNumberWithCommas(amount)}
                onChange={(e) => {
                  const newValue = Number(e.target.value.replace(/,/g, ''));
                  setAmount(newValue > data.barMaximum ? data.barMaximum : newValue);
                }}
                testID={generateTestId(testID, 'amount-input')}
                max={data.barMaximum}
              />
              <CurrencySymbol testID={generateTestId(testID, 'currency-symbol')}>₪</CurrencySymbol>
            </AmountDisplay>
            </SavingAmountContainer>
            <SliderTextsContainer testID={generateTestId(testID, 'slider-texts-container')}>
              <CustomSlider
                value={amount}
                onChange={handleAmountChange}
                min={data.barMinimum}
                max={data.barMaximum}
                step={100}
                testID={generateTestId(testID, 'custom-slider')}
                style={{
                  padding: '0px',
                }}
              />
              <SliderLabels testID={generateTestId(testID, 'slider-labels')}>
                <Typography sx={{ lineHeight: 'normal' }} testID={generateTestId(testID, 'slider-label-max')}>
                  ₪{data.barMaximum.toLocaleString()}
                </Typography>
                <Typography sx={{ lineHeight: 'normal' }} testID={generateTestId(testID, 'slider-label-min')}>
                  ₪{data.barMinimum.toLocaleString()}
                </Typography>
              </SliderLabels>
            </SliderTextsContainer>
          </AmountContainer>
          
          {/* Join Button for desktop */}
          {isDesktop && (
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2,
              mt: 4
            }} testID={generateTestId(testID, 'desktop-join-button-container')}>
              <JoinButton onClick={onClick} testID={generateTestId(testID, 'desktop-join-button')}>{joinButtonText}</JoinButton>
              {data.helpText && data.helpText.length > 0 && (
                <HelpText testID={generateTestId(testID, 'desktop-help-text')} onClick={(event) => {
                  event.stopPropagation();
                  handleTooltipToggle();
                }}>
                  {data.helpText && <HtmlContent content={data.helpText} />}
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <div>
                      {data.helpTooltip && data.helpTooltip.length > 0 && <CustomTooltip
                        PopperProps={{
                          disablePortal: true,
                        }}
                        onClose={handleTooltipClose}
                        open={tooltipOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={<HtmlContent content={data.helpTooltip} />}
                        placement="top"
                        testID={generateTestId(testID, 'desktop-help-tooltip')}
                      >
                        <HelpOutlineIcon 
                          sx={{ fontSize: 20, cursor: 'pointer' }} 
                          onClick={handleTooltipToggle}
                          testID={generateTestId(testID, 'desktop-help-icon')}
                        />
                      </CustomTooltip>}
                    </div>
                  </ClickAwayListener>
                </HelpText>
              )}
            </Box>
          )}
        </CalculatorContainer>

        {isDesktop && (
          <ChartContainer testID={generateTestId(testID, 'desktop-chart-container')}>
            <DoughnutChart 
              slices={slices} 
              totalAmount={amount} 
              savingsAmount={totalSavings} 
              testID={generateTestId(testID, 'desktop-doughnut-chart')}
            />
          </ChartContainer>
        )}

        {!isDesktop && (
          <ChartContainer testID={generateTestId(testID, 'chart-container')} sx={{
            [theme.breakpoints.only('sm')]: {
              // paddingBottom: '',
            },
          }}>
            <DoughnutChart 
              slices={slices} 
              totalAmount={amount} 
              savingsAmount={totalSavings} 
              testID={generateTestId(testID, 'doughnut-chart')}
            />
          </ChartContainer>
        )}

        {/* Join Button for tablet and mobile */}
        {!isDesktop && (
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 2,
            [theme.breakpoints.down('md')]: {
              marginTop: '0px',
              paddingTop: '0px',
              px: 2
            },
          }} testID={generateTestId(testID, 'mobile-join-button-container')}>
            <JoinButton onClick={onClick} testID={generateTestId(testID, 'mobile-join-button')}>{joinButtonText}</JoinButton>
            {data.helpText && data.helpText.length > 0 && (
              <HelpText testID={generateTestId(testID, 'mobile-help-text')} onClick={(event) => {
                event.stopPropagation();
                handleTooltipToggle();
              }} sx={{
                paddingBottom: '24px',
              }}>
                {data.helpText && <HtmlContent content={data.helpText} />}
                <ClickAwayListener onClickAway={handleTooltipClose}>
                  <div>
                    {data.helpTooltip && data.helpTooltip.length > 0 && <CustomTooltip
                      PopperProps={{
                        disablePortal: true,
                      }}
                      onClose={handleTooltipClose}
                      open={tooltipOpen}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={<HtmlContent content={data.helpTooltip} />}
                      placement="top"
                      testID={generateTestId(testID, 'mobile-help-tooltip')}
                    >
                      <HelpOutlineIcon 
                        sx={{ fontSize: 20, cursor: 'pointer' }} 
                        onClick={handleTooltipToggle}
                        testID={generateTestId(testID, 'mobile-help-icon')}
                      />
                    </CustomTooltip>}
                  </div>
                </ClickAwayListener>
              </HelpText>
            )}
          </Box>
        )}
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default SavingsSection; 